/**
 * SpecialistID — Product PDP modular bundle: QUANTITY
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns quantity-input validation and pack-increment rounding (doneTyping).
 * Manages the allowExactQty one-shot flag via SPID.state so event listeners
 * in custom-product.js can set it without coupling to this module's internals.
 *
 * Public API: SPID.doneTyping(), SPID.dropshipSnap(qty, rule),
 * SPID.dropshipStep(qty, rule, dir), SPID.applyDropshipRule(variantId, inventory)
 * Shared state: SPID.state.allowExactQty (boolean, one-shot, default false),
 * SPID.state.dropshipRule ({variantId, doral, pack, dsMin} or null)
 *
 * Cross-module calls: SPID.quantityLogic, SPID.mapMsrpLogic,
 * SPID.updateTablePrice (volume-pricing, currently still in custom-product.js).
 *
 * LOAD ORDER: after custom-product-core.js + custom-product-data.js +
 * custom-product-inventory.js + custom-product-pricing.js, before custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const quantityLogic = SPID.optional('quantityLogic');
  const mapMsrpLogic = SPID.mapMsrpLogic;

  // One-shot flag: when true, doneTyping skips pack-increment rounding so
  // the "set-qty-max" link can set a sub-pack exact quantity (e.g. remaining
  // deny-inventory). Reset to false after each doneTyping run.
  SPID.state.allowExactQty = false;

  // SPID-308: Doral-stock quantity rule for variants that have a pack size.
  //   rule = { variantId, doral, pack, dsMin }
  // 1..doral is free (step 1). Above doral the whole order ships from the supplier,
  // so the quantity must be a multiple of pack AND at least dsMin. Pure functions.
  SPID.state.dropshipRule = null;

  function dropshipLowest(rule) {
    return rule.doral >= 1 ? 1 : rule.dsMin;
  }

  function dropshipSnap(qty, rule) {
    if (!rule) return qty;
    qty = parseInt(qty);
    if (isNaN(qty) || qty < 1) return dropshipLowest(rule);
    if (qty <= rule.doral) return qty;
    return Math.max(rule.dsMin, Math.ceil(qty / rule.pack) * rule.pack);
  }

  function dropshipStep(qty, rule, dir) {
    qty = parseInt(qty);
    if (isNaN(qty) || qty < 1) return dropshipLowest(rule);
    if (dir === 'plus') {
      return qty < rule.doral ? qty + 1 : dropshipSnap(qty + 1, rule);
    }
    if (qty <= rule.doral) return Math.max(1, qty - 1);
    var prevGrid = (Math.ceil(qty / rule.pack) - 1) * rule.pack;
    if (prevGrid >= rule.dsMin && prevGrid > rule.doral) return prevGrid;
    return rule.doral >= 1 ? rule.doral : rule.dsMin;
  }

  // Builds the rule from the API's variant record, or null when the rule does not
  // apply (custom product, no packSize/dropshipMin, or nonsense values).
  function buildDropshipRule(variantId, record) {
    if (!record) return null;
    var isCustom = SPID.getProductMetafield ? SPID.getProductMetafield('custom', 'is_custom') : null;
    if (isCustom === true || isCustom === '1' || isCustom === 1 || isCustom === 'true') return null;
    var pack = parseInt(record.packSize);
    var dsMin = parseInt(record.dropshipMin);
    if (!(pack >= 1) || !(dsMin >= pack)) return null;
    var doral = parseInt((record.inventoryLevels || {})['Specialist ID']);
    if (isNaN(doral) || doral < 0) doral = 0;
    return { variantId: String(variantId), doral: doral, pack: pack, dsMin: dsMin };
  }

  function updateMinimumLabels(lowest) {
    var label = document.querySelector('.quantity__min');
    if (lowest > 1) {
      if (!label) {
        var host = document.querySelector('.quantity__label');
        if (host) {
          label = document.createElement('span');
          label.className = 'quantity__min';
          host.insertBefore(label, host.querySelector('.quantity__rules-cart') || null);
        }
      }
      if (label) label.textContent = '\u00a0· Min. ' + lowest; // same wording as snippets/product-quantity-selector.liquid
    } else if (label) {
      label.textContent = '';
    }
    var minSpan = document.querySelector('.minimum_qty');
    if (minSpan) minSpan.innerHTML = lowest;
  }

  // Called by custom-product-inventory.js once the variant's inventory record is known.
  // With no packSize/dropshipMin on the record this does nothing beyond clearing a stale rule.
  function applyDropshipRule(variantId, record) {
    var rule = buildDropshipRule(variantId, record);
    var prev = SPID.state.dropshipRule;
    if (!rule) {
      if (prev && prev.variantId === String(variantId)) SPID.state.dropshipRule = null;
      return;
    }
    var first = !prev || prev.variantId !== rule.variantId;
    SPID.state.dropshipRule = rule;

    var qtyInput = document.querySelector('.quantity__input');
    if (!qtyInput) return;
    var lowest = dropshipLowest(rule);
    var before = parseInt(qtyInput.value);
    var oldMin = parseInt(qtyInput.getAttribute('min')) || 1;

    // The grid is two-zone, so the browser's min/step arithmetic is out; +/- are
    // handled below and typed values go through dropshipSnap.
    qtyInput.setAttribute('min', lowest);
    qtyInput.setAttribute('data-min', lowest);
    qtyInput.setAttribute('step', 1);

    var next;
    if (first && (isNaN(before) || before === oldMin)) next = lowest;
    else next = dropshipSnap(before, rule);
    if (next !== before) {
      qtyInput.value = next;
      if (typeof SPID.mapMsrpLogic === 'function') SPID.mapMsrpLogic();
      SPID.call('updateTablePrice');
    }
    updateMinimumLabels(lowest);
  }

  // +/- buttons for variants under the rule. Capture phase on document runs before
  // the <quantity-input> element's own click handler, which is stopped for these.
  document.addEventListener('click', function (e) {
    var rule = SPID.state.dropshipRule;
    if (!rule) return;
    var btn = e.target.closest && e.target.closest('.quantity__button');
    if (!btn || (btn.name !== 'plus' && btn.name !== 'minus')) return;
    var wrap = btn.closest('quantity-input');
    var qtyInput = wrap && wrap.querySelector('.quantity__input');
    if (!qtyInput || !qtyInput.closest('.price-per-item__container')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var previous = qtyInput.value;
    qtyInput.value = dropshipStep(qtyInput.value, rule, btn.name);
    if (previous !== qtyInput.value) qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  SPID.dropshipSnap = dropshipSnap;
  SPID.dropshipStep = dropshipStep;
  SPID.applyDropshipRule = applyDropshipRule;

  function doneTyping() {
    const qtyInput = document.querySelector('.quantity__input');
    const min = parseInt(qtyInput?.getAttribute('min')) || 1;
    let qty = parseInt((qtyInput?.value || '').replace(/\D/g, ''));
    const increment = parseInt(qtyInput?.getAttribute('step')) || 1;

    if (isNaN(qty) || qty < min) {
      qty = min;
      if (qtyInput) qtyInput.value = min;
    }

    const stock = parseInt(qtyInput?.getAttribute('max'));
    if (!isNaN(stock)) {
      if (stock > 0 && qty > stock) {
        if (qtyInput) qtyInput.value = stock;
        qty = stock;
      }
    }

    // SPID-308: Doral-stock rule. A typed value goes to the nearest valid quantity at
    // or above it; the set-qty-max one-shot is left exactly as typed.
    if (SPID.state.dropshipRule && !SPID.state.allowExactQty) {
      qty = dropshipSnap(qty, SPID.state.dropshipRule);
      if (qtyInput) qtyInput.value = qty;
    }

    quantityLogic();
    mapMsrpLogic();
    SPID.call('updateTablePrice');

    if (qty < min) {
      if (qtyInput) qtyInput.value = min;
    } else if (!SPID.state.allowExactQty && increment > 1) {
      // SPID-273: round onto the same grid the +/- buttons step on. The browser's
      // stepUp()/stepDown() count from the min attribute, so the valid quantities
      // are min, min + increment, min + 2*increment... Rounding to bare multiples
      // of the increment instead used to fight the buttons whenever min was not
      // itself a multiple (e.g. min 25 with 100-packs: + gave 125, this rounded it
      // back to 100, + gave 125 again - the quantity could never leave 25/100).
      const steps = Math.max(0, Math.round((qty - min) / increment));
      let roundedQty = min + steps * increment;

      // Never round up past available stock; drop to the previous valid step.
      if (!isNaN(stock) && stock > 0 && roundedQty > stock) {
        roundedQty = Math.max(min, roundedQty - increment);
      }

      if (qtyInput && roundedQty !== qty) qtyInput.value = roundedQty;
    }
    SPID.state.allowExactQty = false;
  }

  SPID.doneTyping = doneTyping;

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('quantity');
})();
